import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { buildSelectOptions } from '../ui/listFilterHelpers';
import {
  collectDesignCategories,
  collectDesignConcepts,
  countActiveDesignFilters,
  DEFAULT_DESIGN_LIST_FILTERS,
  getDesignBeamTypeOptions,
} from './designListFilterUtils';

const fieldClass = 'rounded-md py-2.5';

/**
 * Multi-criteria design filters. Combine fields, then Apply (AND logic).
 */
export function DesignListFiltersPanel({
  value,
  onApply,
  items = [],
  className = '',
  title = 'Filter designs',
}) {
  const [draft, setDraft] = useState(() => ({ ...DEFAULT_DESIGN_LIST_FILTERS }));

  useEffect(() => {
    setDraft({ ...DEFAULT_DESIGN_LIST_FILTERS, ...value });
  }, [value]);

  const draftCount = useMemo(() => countActiveDesignFilters(draft), [draft]);

  const conceptOptions = useMemo(
    () => buildSelectOptions('All concepts', collectDesignConcepts(items), draft.concept),
    [items, draft.concept]
  );

  const categoryOptions = useMemo(
    () => buildSelectOptions('All categories', collectDesignCategories(items), draft.category),
    [items, draft.category]
  );

  const beamTypeOptions = useMemo(
    () => getDesignBeamTypeOptions().map((o) => ({ value: o.value, label: o.label })),
    []
  );

  const handleField = (key) => (e) => {
    setDraft((d) => ({ ...d, [key]: e.target.value }));
  };

  const reset = () => {
    const cleared = { ...DEFAULT_DESIGN_LIST_FILTERS };
    setDraft(cleared);
    onApply(cleared);
  };

  const apply = () => {
    onApply({ ...DEFAULT_DESIGN_LIST_FILTERS, ...draft });
  };

  return (
    <div
      className={[
        'rounded-lg border border-slate-200/90 bg-slate-100/90 p-3 sm:p-4 md:p-5',
        '[&_label]:text-xs [&_label]:text-slate-800',
        className,
      ].join(' ')}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">
          {title}
          {draftCount > 0 ? (
            <span className="ml-2 text-xs font-medium text-slate-500">
              {draftCount} active
            </span>
          ) : null}
        </p>
        <button
          type="button"
          onClick={reset}
          className="text-xs font-semibold text-slate-600 transition hover:text-slate-900"
        >
          Reset all
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Input
          type="search"
          label="Search"
          placeholder="Beam, picks, cards, read count…"
          value={draft.search}
          onChange={handleField('search')}
          className={fieldClass}
        />
        <Input
          type="text"
          label="Design name"
          placeholder="Design number"
          value={draft.sampleNo}
          onChange={handleField('sampleNo')}
          className={fieldClass}
        />
        <Select
          label="Concept"
          value={draft.concept}
          onChange={handleField('concept')}
          options={conceptOptions}
          className={fieldClass}
        />
        <Select
          label="Category"
          value={draft.category}
          onChange={handleField('category')}
          options={categoryOptions}
          className={fieldClass}
        />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-12 xl:items-end">
        <div className="xl:col-span-4">
          <Select
            label="Beam type"
            value={draft.beamType}
            onChange={handleField('beamType')}
            options={beamTypeOptions}
            className={fieldClass}
          />
        </div>
        <div className="flex items-end justify-start xl:col-span-8 xl:justify-end">
          <Button
            type="button"
            onClick={apply}
            className="h-11 w-full min-w-[8.5rem] rounded-md px-8 text-base font-semibold shadow-sm sm:w-auto"
          >
            Filter
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DesignListFiltersPanel;
